import React from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { SearchInput } from 'ui/standard/input/search_input/search_input';

import {
  RemovableKindEntry,
  SearchKindEntry,
  SearchKindEntryProps,
} from 'ui/issues/kind_entry/kind_entry';

import { IssueKind } from 'services/pangolin/types';
import { getIssueKindTitle } from 'services/pangolin/logic/issues/issues';

import baseStyles from './kind_input.module.css';
import { KindInputStore } from './kind_input_store';

type KindInputStyles = {
  inputButton: string;
  plusIcon: string;
  placeholder: string;
};

const themedStyles: ThemedStyles<KindInputStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    plusIcon: baseStyles.daylightPlusIcon,
    placeholder: baseStyles.daylightPlaceholder,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    plusIcon: baseStyles.midnightPlusIcon,
    placeholder: baseStyles.midnightPlaceholder,
  },
};

type KindEntryDisplayProps = {
  store: KindInputStore;
  kind: IssueKind;
};

const KindEntryDisplay = observer(({ store, kind }: KindEntryDisplayProps) => {
  const onRemoveClick = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.removeEntry(kind);
  }), [store, kind]);

  return <RemovableKindEntry kind={kind} onRemoveClick={onRemoveClick}/>;
});

type KindInputDisplayProps = {
  store: KindInputStore;
  placeholder: string;
};

const KindInputDisplay = observer(({ store, placeholder }: KindInputDisplayProps) => {
  const styles = useStyles(themedStyles);
  const onShowModal = React.useCallback(action((event: ButtonClickEvent) => {
    event.stopPropagation();
    store.search.search.setValue('');
    store.search.modal.showModal();
  }), [store]);
  return (
    <>
      <button type='button' className={styles.inputButton} onClick={onShowModal}>
        <div className={styles.placeholder}>
          {placeholder}
        </div>
        <div className={styles.plusIcon}/>
      </button>
      {store.entries.map((kind) =>
        <KindEntryDisplay store={store} kind={kind} key={kind}/>)}
    </>
  );
});

type KindInputProps = {
  placeholder: string;
  store: KindInputStore;
};

@observer export class KindInput extends React.Component<KindInputProps> {
  @computed get currentEntryKinds(): Map<IssueKind, true> {
    const map: Map<IssueKind, true> = new Map();
    const ids = this.props.store.entries;
    ids.forEach((id) => map.set(id, true));
    return map;
  }

  filterEntry = (kind: IssueKind, text: string): boolean => {
    if (this.currentEntryKinds.has(kind)) {
      return false;
    }
    const trimmed = text.trim().toLocaleLowerCase();
    if (trimmed) {
      const label = getIssueKindTitle(kind).toLocaleLowerCase();
      return label.indexOf(trimmed) >= 0;
    }
    return true;
  };

  DisplayComponent = () => {
    return <KindInputDisplay store={this.props.store} placeholder={this.props.placeholder} />;
  };

  ItemComponent = ({ data: kind, ...restProps }: Omit<SearchKindEntryProps, 'onSelect'>) => {
    const onSelect = React.useCallback(action(() => {
      this.props.store.commitEntry(kind);
      this.props.store.search.modal.hideModal();
    }), [this.props.store, kind]);
    return <SearchKindEntry data={kind} onSelect={onSelect} {...restProps}/>;
  };

  render() {
    return (
      <SearchInput
        placeholder={this.props.placeholder}
        store={this.props.store.search}
        data={this.props.store.options}
        filterFunc={this.filterEntry}
        keyFunc={(k) => IssueKind[k]}
        ItemComponent={this.ItemComponent}
        DisplayComponent={this.DisplayComponent}
        selectOnly={false}
      />
    );
  }
}
